import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, CardBody, CardHeader, Col, Form, Pagination, Row, Table } from '@edgebox/react-components';
import { useNavigate } from 'react-router-dom';
import { IPagedListResponse, InternalId } from '@edgebox/data-definition-kit';
import { AiPromptExecutionStatus } from '@edgebox/sync-core-data-definitions';
import { ClientAiPromptExecutionEntity } from '@edgebox/sync-core-rest-client';
import { enumValues, formatDateTime, formatMoney, normalizeFilterId, StatusFilter, useSyncCoreApi } from '../aiHelpers';
import { AiModelName } from '../components/AiModelName';
import { AiPromptTemplateReferenceName } from '../components/AiPromptTemplateReferenceName';
import { AiProviderName } from '../components/AiProviderName';
import { ErrorCard } from '../components/ErrorCard';
import { LoadingCard } from '../components/LoadingCard';
import { ScreenHeader } from '../components/ScreenHeader';

function isExecutionRunning(status: AiPromptExecutionStatus): boolean {
  return status === AiPromptExecutionStatus.RunningSync || status === AiPromptExecutionStatus.RunningAsync;
}

function executionStatusVariant(status: AiPromptExecutionStatus): 'success' | 'primary' | 'danger' | 'secondary' {
  if (status === AiPromptExecutionStatus.Succeeded) return 'success';
  if (isExecutionRunning(status)) return 'primary';
  if (status === AiPromptExecutionStatus.Failed) return 'danger';
  return 'secondary';
}

export function AiExecutionsView() {
  const api = useSyncCoreApi();
  const navigate = useNavigate();
  const itemsPerPage = 20;

  const [statusFilter, setStatusFilter] = useState<StatusFilter<AiPromptExecutionStatus>>('Default');
  const [providerId, setProviderId] = useState<string>('');
  const [modelId, setModelId] = useState<string>('');
  const [templateId, setTemplateId] = useState<string>('');
  const [templateKey, setTemplateKey] = useState<string>('');
  const [customerId, setCustomerId] = useState<string>('');
  const [projectId, setProjectId] = useState<string>('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const [response, setResponse] = useState<IPagedListResponse<ClientAiPromptExecutionEntity> | undefined>(undefined);
  const [page, setPage] = useState(0);

  const filters = useMemo(() => {
    return {
      status: statusFilter === 'Default' ? undefined : statusFilter,
      providerId: normalizeFilterId(providerId),
      modelId: normalizeFilterId(modelId),
      templateId: normalizeFilterId(templateId),
      templateKey: templateKey.trim().length ? templateKey.trim() : undefined,
      customerId: normalizeFilterId(customerId),
      projectId: normalizeFilterId(projectId),
    };
  }, [statusFilter, providerId, modelId, templateId, templateKey, customerId, projectId]);

  useEffect(() => {
    setPage(0);
  }, [filters]);

  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    setLoading(true);
    setError(undefined);

    api.ai.promptExecutions
      .list({ page, itemsPerPage }, filters)
      .then((res) => {
        if (cancelled) return;
        setResponse(res);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, filters, page]);

  const items = useMemo(() => {
    return [...(response?.items ?? [])].sort((a, b) => {
      const aTime = a.createdAt ? new Date(String(a.createdAt)).getTime() : 0;
      const bTime = b.createdAt ? new Date(String(b.createdAt)).getTime() : 0;
      return bTime - aTime;
    });
  }, [response?.items]);
  const totalPages = Math.max(1, response?.numberOfPages ?? 0);
  const currentPage = response?.page ?? page;
  const canGoPrevious = currentPage > 0;
  const canGoNext = currentPage < totalPages - 1;

  return (
    <>
      <ScreenHeader title="Executions" />

      <Card className="mb-3">
        <CardBody>
          <Form>
            <Row className="g-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={statusFilter === 'Default' ? 'Default' : 'Custom'}
                    onChange={(e) => setStatusFilter(e.target.value === 'Default' ? 'Default' : [])}
                  >
                    <option value="Default">Default (Any)</option>
                    <option value="Custom">Custom</option>
                  </Form.Select>
                </Form.Group>

                {statusFilter !== 'Default' && (
                  <div className="mt-2">
                    {enumValues(AiPromptExecutionStatus).map((s) => (
                      <Form.Check
                        key={s}
                        type="checkbox"
                        label={s}
                        checked={(statusFilter as AiPromptExecutionStatus[]).includes(s)}
                        onChange={(e) => {
                          const current = statusFilter as AiPromptExecutionStatus[];
                          setStatusFilter(e.target.checked ? [...current, s] : current.filter((x) => x !== s));
                        }}
                      />
                    ))}
                  </div>
                )}
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Provider ID</Form.Label>
                  <Form.Control value={providerId} onChange={(e) => setProviderId(e.target.value)} placeholder="Optional" />
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Model ID</Form.Label>
                  <Form.Control value={modelId} onChange={(e) => setModelId(e.target.value)} placeholder="Optional" />
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Template ID</Form.Label>
                  <Form.Control value={templateId} onChange={(e) => setTemplateId(e.target.value)} placeholder="Optional" />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Template key</Form.Label>
                  <Form.Control value={templateKey} onChange={(e) => setTemplateKey(e.target.value)} placeholder="Optional" />
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Customer ID</Form.Label>
                  <Form.Control value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="Optional" />
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>Project ID</Form.Label>
                  <Form.Control value={projectId} onChange={(e) => setProjectId(e.target.value)} placeholder="Optional" />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </CardBody>
      </Card>

      {loading ? <LoadingCard text="Loading executions…" /> : null}
      {error ? <ErrorCard error={error} /> : null}

      <Card>
        <CardHeader>
          Executions <Badge bg="secondary">{response?.totalNumberOfItems ?? items.length}</Badge>
        </CardHeader>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <small className="text-muted">
              Page {currentPage + 1} / {totalPages}
            </small>
            <Pagination className="mb-0">
              <Pagination.Prev disabled={!canGoPrevious || loading} onClick={() => setPage((value) => Math.max(0, value - 1))} />
              <Pagination.Next disabled={!canGoNext || loading} onClick={() => setPage((value) => (canGoNext ? value + 1 : value))} />
            </Pagination>
          </div>

          <Table striped bordered hover size="sm" responsive className="mb-0">
            <thead>
              <tr>
                <th>Status</th>
                <th>Created</th>
                <th>Provider</th>
                <th>Model</th>
                <th>Template</th>
                <th>Duration</th>
                <th>Retries</th>
                <th>Batched</th>
                <th>Tokens</th>
                <th>Costs</th>
                <th style={{ width: '1%' }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id}>
                  <td>
                    <Badge bg={executionStatusVariant(e.status)}>{e.status}</Badge>
                  </td>
                  <td>{formatDateTime(e.createdAt)}</td>
                  <td>
                    <AiProviderName id={e.provider?.getId?.()} />
                  </td>
                  <td>
                    <AiModelName id={e.model?.getId?.()} />
                  </td>
                  <td>
                    <AiPromptTemplateReferenceName templateKey={e.promptTemplate?.getStableKey?.()} />
                  </td>
                  <td>{e.durationMs ? `${e.durationMs} ms` : '-'}</td>
                  <td>{e.retryCount ?? '-'}</td>
                  <td>{e.isBatched ? 'Yes' : 'No'}</td>
                  <td>{e.usage?.tokens !== undefined ? e.usage.tokens.toLocaleString() : '-'}</td>
                  <td>{e.usage?.costs !== undefined ? formatMoney(e.usage.costs) : '-'}</td>
                  <td>
                    <Button size="sm" variant="light" onClick={() => navigate(`/ai/prompt-execution/${e.id}`)}>
                      Details
                    </Button>
                  </td>
                </tr>
              ))}
              {!items.length && (
                <tr>
                  <td colSpan={11} className="text-muted">
                    No executions found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </>
  );
}

export function useExecutionIdParam(idParam: string | undefined): InternalId | undefined {
  return idParam?.trim().length ? (idParam as InternalId) : undefined;
}
